import axios from "axios";
import type {
  QuotationRequestRQ,
  QuotationRequestSessionType,
} from "../../components/modals/NewQuotationRequestModal/definitions";
import { sisikong_url } from "..";
import type { SubmitQuotationRequestRQ, SubmitQuotationRequestRS } from "./types";

// const baseUrl = "http://localhost:8888/sophon-evo";
const baseUrl = `${sisikong_url}/sophon-evo`;

export function getQuotations({ token, params }: { token: string; params: any }) {
  return axios.get(`${sisikong_url}/crs/external/quotationrequest/`, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function createQuotationUploadMode({ file }: { file: File }) {
  const formData = new FormData();
  formData.append("file", file);

  const url = `${baseUrl}/api/v1/create-quotation-request/upload-mode`;
  return fetch(url, { method: "POST", body: formData });
}

export async function createQuotationChat({
  message,
  chat_session_uid,
  source_entity,
  username,
  user_email,
}: {
  message: string;
  chat_session_uid: string;
  source_entity: string;
  username: string;
  user_email: string;
}): Promise<Response | null> {
  const url = `${baseUrl}/api/v1/quotation-request-chat`;
  return fetch(url, {
    method: "POST",
    body: JSON.stringify({ message, chat_session_uid, source_entity, username, user_email }),
    headers: { "Content-Type": "application/json" },
  })
    .then((response) => {
      if (response.status === 400) {
        return null;
      }
      return response;
    })
    .catch(() => {
      return null;
    });
}

export function createQuotationV2({
  payload,
}: {
  payload: QuotationRequestRQ;
}): Promise<{ data: { session_id: string } }> {
  return axios.post(`${baseUrl}/api/v1/create-quotation-request`, payload);
}

export function createQuotationV3({
  payload,
}: {
  payload: SubmitQuotationRequestRQ;
}): Promise<{ data: SubmitQuotationRequestRS }> {
  return axios.post(`${baseUrl}/api/v1/submit-quotation-request`, payload);
}

export function pollQuotationRequest(
  session_id: string,
): Promise<{ data: QuotationRequestSessionType }> {
  return axios.get(`${baseUrl}/api/v1/quotation-request/${session_id}`);
}

export function submitQuotationRequest({
  session_id,
  chat_session_uid,
  token,
  extra_comments,
  source_entity,
  target_entity,
  quotation_title,
  due_date,
}: {
  session_id: string;
  chat_session_uid: string;
  token: string;
  extra_comments: string;
  source_entity: string;
  target_entity: string;
  quotation_title: string;
  due_date: string;
}): Promise<{
  data: {
    quotation_request_id: number;
    quotation_request_reference: string;
    session_id: string;
  };
}> {
  const headers = { Authorization: `Bearer ${token}` };

  const payload = {
    session_id,
    extra_comments,
    chat_session_uid,
    source_entity,
    target_entity,
    quotation_title,
    due_date,
  };
  return axios.post(`${baseUrl}/api/v1/submit-quotation-request`, payload, { headers });
}

export function excludeService(
  session_id: string,
  service_id: string,
): Promise<{ data: { service_id: string; session_id: string } }> {
  return axios.post(`${baseUrl}/api/v1/exclude-service`, { session_id, service_id });
}

export function reIncludeService(
  session_id: string,
  service_id: string,
): Promise<{ data: { service_id: string; session_id: string } }> {
  return axios.post(`${baseUrl}/api/v1/reinclude-service`, { session_id, service_id });
}

// Chat Attachments ============================================================

type CHAT_ATTACHMENT_MIME_TYPES =
  | "application/pdf"
  | "application/msword"
  | "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  | "application/vnd.ms-excel"
  | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  | "application/vnd.ms-powerpoint"
  | "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  | "text/plain"
  | "text/csv"
  | "text/rtf"
  | "application/rtf";

export const CHAT_ATTACHMENT_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "text/rtf",
  "application/rtf",
];

type QuotationRequestChatAttachmentsRQ = {
  chat_session_uid: string;
  file: File;
  source_entity: string;
  username: string;
  user_email: string;
};

type QuotationRequestChatAttachmentsRS = {
  session_uid: string;
};

export function createQuotationChatAttachment({
  payload,
}: {
  payload: QuotationRequestChatAttachmentsRQ;
}): Promise<{ data: QuotationRequestChatAttachmentsRS }> {
  const formData = new FormData();

  formData.append("files", payload.file);
  formData.append("source_entity", payload.source_entity);
  formData.append("username", payload.username);
  formData.append("user_email", payload.user_email);
  return axios.post(`${baseUrl}/api/v1/quotation-request-chat/attachments`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function createQuotationChatAttachmentWithSessionUid({
  chat_session_uid,
  file,
}: {
  chat_session_uid: string;
  file: File;
}): Promise<{ data: QuotationRequestChatAttachmentsRS }> {
  const formData = new FormData();
  formData.append("file", file);
  return axios.post(
    `${baseUrl}/api/v1/quotation-request-chat/${chat_session_uid}/attachments`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
}

export type ChatAttachment = {
  uid: string;
  mime_type: CHAT_ATTACHMENT_MIME_TYPES;
  parsed_data: string | null;
  filename: string;
  size: number;
};

type QuotationRequestChatAttachmentsListRS = {
  attachments: ChatAttachment[];
};

export function getQuotationRequestChatAttachmentsList({
  chat_session_uid,
}: {
  chat_session_uid: string;
}): Promise<{ data: QuotationRequestChatAttachmentsListRS }> {
  return axios.get(`${baseUrl}/api/v1/quotation-request-chat/${chat_session_uid}/attachments`);
}

type QuotationRequestChatAttachmentDeleteRS = {
  success: boolean;
  message: string;
};

export function deleteQuotationRequestChatAttachment({
  chat_session_uid,
  attachment_uid,
}: {
  chat_session_uid: string;
  attachment_uid: string;
}): Promise<{ data: QuotationRequestChatAttachmentDeleteRS }> {
  return axios.delete(
    `${baseUrl}/api/v1/quotation-request-chat/${chat_session_uid}/attachments/${attachment_uid}`,
  );
}
